import React, { useEffect, useRef, useState } from 'react';
import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Props {
  /** Conteúdo do popover de ajuda (texto/JSX explicando a tela). */
  children: React.ReactNode;
  /** Largura máxima do popover em px (default 420). */
  largura?: number;
}

/**
 * Botão "?" de ajuda ao lado do título da página + popover. Padrão único de
 * toda página: abre no clique, fecha em clique-fora/Esc. Extraído de
 * FornecedorGestaoPage/MarcaGestaoPage pra reuso em todas as telas.
 */
const AjudaTitulo: React.FC<Props> = ({ children, largura = 420 }) => {
  const [aberta, setAberta] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!aberta) return;
    const onClick = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setAberta(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberta(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [aberta]);

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => setAberta((v) => !v)}
        aria-label="Como usar esta tela"
        title="Como usar esta tela"
        className="rounded p-0.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
      >
        <QuestionMarkCircleIcon className="h-4 w-4" />
      </button>

      {aberta && (
        <div
          ref={popRef}
          role="dialog"
          style={{ width: `min(${largura}px, calc(100vw - 2rem))` }}
          className="absolute left-0 top-full z-30 mt-2 rounded-lg border border-zinc-200 bg-white p-3 text-xs font-normal leading-relaxed text-zinc-700 shadow-lg dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
        >
          <button
            type="button"
            onClick={() => setAberta(false)}
            aria-label="Fechar"
            className="absolute right-1.5 top-1.5 rounded p-0.5 text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
          <div className="pr-5">{children}</div>
        </div>
      )}
    </span>
  );
};

export default AjudaTitulo;
